import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Camera, Image as ImageIcon, X } from "lucide-react";
import TopBar from "@/components/TopBar";
import { useOutfitDraft } from "@/context/OutfitDraftContext";

// Resize + transcode helper. Reads File -> returns {base64, mime} bounded by max dimension.
function fileToResizedBase64(file, maxDim = 1280) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read file"));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("Could not decode image"));
      img.onload = () => {
        let { width, height } = img;
        const scale = Math.min(1, maxDim / Math.max(width, height));
        width = Math.round(width * scale);
        height = Math.round(height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
        resolve({ base64: dataUrl, mime: "image/jpeg" });
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

export default function UploadScreen() {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const { draft, update } = useOutfitDraft();
  const source = search.get("source") || "gallery"; // 'camera' or 'gallery'
  const fileInputRef = useRef(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // Auto-open the file picker on first mount based on source
  useEffect(() => {
    if (fileInputRef.current && !draft.imageBase64) {
      // Small delay so the back button animation finishes
      const t = setTimeout(() => fileInputRef.current?.click(), 200);
      return () => clearTimeout(t);
    }
  }, [source]); // eslint-disable-line

  const onFile = async (e) => {
    setError("");
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    if (!/image\/(jpeg|png|webp|jpg)/i.test(file.type)) {
      setError("Please choose a JPEG, PNG, or WEBP image.");
      return;
    }
    setBusy(true);
    try {
      const { base64, mime } = await fileToResizedBase64(file, 1280);
      update({ imageBase64: base64, imageMime: mime });
    } catch (err) {
      setError(err.message || "Could not process image.");
    } finally {
      setBusy(false);
    }
  };

  const openPicker = () => fileInputRef.current?.click();

  const clearImage = () => update({ imageBase64: null });

  const next = () => navigate("/appearance");

  const titleEyebrow = source === "camera" ? "CAMERA" : "GALLERY";

  return (
    <div className="screen fade-in" data-testid="upload-screen">
      <TopBar title="STEP 1 OF 3" />
      <div style={{ marginBottom: 22 }}>
        <div className="eyebrow" style={{ marginBottom: 10 }}>{titleEyebrow}</div>
        <h2 className="h1" style={{ fontSize: 34 }}>
          Add a <em className="serif" style={{ fontStyle: "italic", fontWeight: 300 }}>photo</em>
        </h2>
        <p className="body" style={{ marginTop: 12 }}>
          A full-body photo gives the best results. We use it only to tailor your outfit.
        </p>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        capture={source === "camera" ? "environment" : undefined}
        onChange={onFile}
        style={{ display: "none" }}
        data-testid="upload-file-input"
      />

      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 14 }}>
        {draft.imageBase64 ? (
          <div style={{ position: "relative" }} data-testid="upload-preview-wrap">
            <img src={draft.imageBase64} alt="preview" className="preview-img" data-testid="upload-preview-img" />
            <button
              type="button"
              className="icon-btn"
              onClick={clearImage}
              aria-label="Remove photo"
              data-testid="upload-clear-btn"
              style={{ position: "absolute", top: 10, right: 10, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(8px)" }}
            >
              <X size={16} />
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="tile"
            onClick={openPicker}
            data-testid="upload-placeholder-btn"
            style={{ alignItems: "center", justifyContent: "center", minHeight: 280, gap: 10 }}
          >
            <div className="tile-icon" style={{ width: 56, height: 56 }}>
              {source === "camera" ? <Camera size={26} /> : <ImageIcon size={26} />}
            </div>
            <div className="h3" style={{ marginTop: 4 }}>
              {busy ? "Loading…" : (source === "camera" ? "Open camera" : "Choose from gallery")}
            </div>
            <div className="tiny" style={{ color: "rgba(255,255,255,0.5)" }}>JPEG, PNG, or WEBP</div>
          </button>
        )}

        {error && <div className="error-text" data-testid="upload-error">{error}</div>}

        <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
          <button
            type="button"
            className="btn btn-outline"
            onClick={openPicker}
            data-testid="upload-replace-btn"
          >
            {draft.imageBase64 ? "Choose different photo" : (source === "camera" ? "Use gallery instead" : "Take a new photo")}
          </button>
        </div>
      </div>

      <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 10 }}>
        <button
          className="btn btn-primary"
          disabled={!draft.imageBase64}
          onClick={next}
          data-testid="upload-next-btn"
        >
          Continue
        </button>
        <button
          className="btn btn-ghost"
          onClick={() => { update({ imageBase64: null, appearance: null }); navigate("/style-pick"); }}
          data-testid="upload-skip-btn"
        >
          Skip photo — choose style only
        </button>
      </div>
    </div>
  );
}
